'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ScrollReveal from './ScrollReveal';
import { ArrowLeft, ArrowRight, Star } from 'lucide-react';

// Import images
const ASSET_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';
const imgProf1 = `${ASSET_BASE}/assets/web/reviews/prof_1.webp`;
const imgProf2 = `${ASSET_BASE}/assets/web/reviews/prof_2.webp`;
const imgProf3 = `${ASSET_BASE}/assets/web/reviews/prof_3.webp`;
const imgRev1 = `${ASSET_BASE}/assets/web/reviews/review_1.webp`;
const imgRev2 = `${ASSET_BASE}/assets/web/reviews/review_2.webp`;
import { analytics } from '../services/analytics';

const testimonials = [
    {
        name: "Jason Miller",
        role: "Product Analyst, FinTech Startup",
        image: imgProf1.src || imgProf1,
        quote: "Practicing interviews here made a huge difference. I felt more confident and structured during real interviews."
    },
    {
        name: "Emily Carter",
        role: "UX Designer, SaaS Company",
        image: imgProf2.src || imgProf2,
        quote: "The resume suggestions were spot on. My profile finally started getting shortlists after applying."
    },
    {
        name: "Daniel Brooks",
        role: "Software Engineer, Product Company",
        image: imgProf3.src || imgProf3,
        quote: "Mock interviews and communication feedback helped me identify exactly what to improve."
    },
    {
        name: "Sarah Thompson",
        role: "Marketing Manager, EdTech",
        image: imgRev1.src || imgRev1,
        quote: "Having AI tools with mentorship in one place made career prep effective and faster."
    },
    {
        name: "Michael Chen",
        role: "Data Scientist, Tech Corp",
        image: imgRev2.src || imgRev2,
        quote: "The structured learning path and real-time feedback loop accelerated my transition into data science."
    }
];

const Testimonials = () => {
    const scrollContainerRef = useRef(null);

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 400;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section className="py-24 bg-white dark:bg-transparent relative overflow-hidden transition-colors duration-300">

            {/* Header with Navigation */}
            <div className="container flex flex-col md:flex-row items-center md:items-end justify-between mb-12 gap-6">{/* 960px max-width */}
                <div className="max-w-3xl text-center md:text-left">
                    <h2 className="text-brand-600 dark:text-brand-400 font-bold tracking-widest uppercase text-[10px] md:text-xs mb-3">
                        WHAT PROFESSIONALS SAY
                    </h2>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4 leading-tight tracking-tight">
                        Trusted by learners at every stage
                    </h3>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl font-normal opacity-90">
                        Real feedback from people using our platform to grow, prepare, and get hired.
                    </p>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-4">
                    <button
                        onClick={() => scroll('left')}
                        className="w-12 h-12 rounded-full border border-gray-200 dark:border-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-300"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="w-12 h-12 rounded-full border border-gray-200 dark:border-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-300"
                    >
                        <ArrowRight size={20} />
                    </button>
                </div>
            </div>

            {/* Horizontal Scroll Container */}
            <div
                ref={scrollContainerRef}
                className="flex gap-6 overflow-x-auto pb-12 snap-x snap-mandatory scrollbar-hide px-4 md:px-8"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {testimonials.map((testimonial, index) => (
                    <ScrollReveal
                        key={index}
                        direction={index % 2 === 0 ? 'top' : 'bottom'}
                        delay={index * 0.15}
                        className="flex-shrink-0"
                    >
                        <div
                            onClick={() => analytics.trackCardClick('Testimonials', testimonial.name)}
                            className="min-w-[300px] md:min-w-[350px] h-[500px] relative rounded-[2rem] overflow-hidden snap-center group cursor-pointer"
                        >
                            {/* Background Image */}
                            <img
                                src={testimonial.image}
                                alt={testimonial.name}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />

                            {/* Gradient Overlay */}
                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                            {/* Content Card Overlay */}
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="bg-white/10 dark:bg-black/20 backdrop-blur-md border border-white/20 dark:border-white/10 rounded-2xl p-6 shadow-lg">
                                    <p className="text-white/90 text-[15px] leading-relaxed mb-6 font-medium">
                                        "{testimonial.quote}"
                                    </p>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">
                                            {testimonial.name}
                                        </h4>
                                        <p className="text-white/60 text-xs">
                                            {testimonial.role}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>
                ))}
            </div>

        </section>
    );
};

export default Testimonials;
